import {useMuseumIdsWithNbAccess} from "./useMuseumIdsWithNbAccess";
import {useEffect, useMemo, useState} from "react";
import {publishingTagsSearchHandler} from "../publishing-tags/publishingTagsSearchHandler";
import {Stack} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import PublishIcon from '@mui/icons-material/Publish';
import {preventPublishingToNbIfNotAllowed} from "./preventPublishingToNbIfNotAllowed";
import {addPublishingTagsToModel} from "../tags/addPublishingTagsToModel";
import {useAuthsState} from "../auths/authsContext";
import {useMyMuseums} from "../museum/useMyMuseums";
import {clientLog} from "../clientLog";
import {publishToNbBatch} from "./publishToNb";
import {PromisePool} from "@supercharge/promise-pool";
import LinearProgress from "@mui/material/LinearProgress";
import {useMuseumsWithM24Access} from "./useMuseumsWithM24Access";
import Grow from "@mui/material/Grow";
import {MODELS_SAVED, SET_SEARCHING, useSearchDispatch} from "../search/SearchContext";
import {MODEL_SAVED, useSearchResultDetailsDispatch} from "../search-result-details/SearchResultDetailsContext";
import {useSnackbar} from "notistack";
import {damsFetch} from "../app/damsFetch";
import decamelizeKeysDeep from "decamelize-keys-deep";

export const PublishToSystemTable = ({t, models, callback, disableCallback, batchMode}) => {

    const {enqueueSnackbar} = useSnackbar();
    const {userData: user, damsId} = useAuthsState();
    const {museumCollections} = useAuthsState();

    const myMuseums = useMyMuseums({});
    const {getMuseumsWithNbAccess} = useMuseumIdsWithNbAccess();
    const {getMuseumsWithM24Access} = useMuseumsWithM24Access();

    const [loading, setLoading] = useState(true); // State to track loading status.
    const [museums, setMuseums] = useState([]); // State to store available museums.
    const [options, setOptions] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState([]); // State to store selected tags.
    const [publishing, setPublishing] = useState(new Set()); // Labels of the systems with a publish in flight.
    const [nbProgress, setNbProgress] = useState(null); // {completed, total} while an NB publish job runs.
    const [unpublishing, setUnpublishing] = useState(false);

    const [publishedThisSession, setPublishedThisSession] = useState([]); // State to track items published in this session.

    const searchDispatch = useSearchDispatch();
    const searchResultDetailsDispatch = useSearchResultDetailsDispatch();

    const getPublishTags = (document) => document?.content?.publish?.tags || [];

    const showPublishWarning = () => {
        const warningMessage = t('warnNoNbAccess',
            'Advarsel: Dokumentet er utelatt fordi du ikke har tilgang til å ' +
            'publisere til NB for alle museene som er knyttet til det valgte dokumentet.');
        enqueueSnackbar(warningMessage, {variant: 'warning'});
    };

    const showNbFailures = (count, total) => {
        enqueueSnackbar(t('publishToNbFailedSummary',
            `${count} av ${total} dokumenter kunne ikke publiseres til Nasjonalbiblioteket. NB-taggen er fjernet fra dem.`),
            {variant: 'error'});
    };

    const showNbOutcomeUnknown = (count, total) => {
        enqueueSnackbar(t('publishToNbOutcomeUnknown',
            `Utfallet er ukjent for ${count} av ${total} dokumenter: publiseringen til Nasjonalbiblioteket pågår ` +
            'fortsatt. Sjekk status i NB Publisering om litt.'),
            {variant: 'warning'});
    };

    const publishToSystem = async (system) => {
        if (!system && callback && typeof callback === 'function') {
            callback();
        }

        let selectedSystem = {...system, inputValue: system.label};
        const documentsToTag = preventPublishingToNbIfNotAllowed(getMuseumsWithNbAccess(), [selectedSystem], Array.isArray(models) ? models : [models]);
        if (documentsToTag.length === 0) {
            showPublishWarning()
            if (callback && typeof callback === 'function') {
                callback();
            }
        }

        if (disableCallback && typeof disableCallback === 'function') {
            // If running in batch-mode, disable the close dialog button while processing:
            disableCallback();
        }

        setSelectedOptions(prev => [...prev, ...documentsToTag]);

        let publishingErrors = [];
        const taggedDocuments = [];

        const tagDocument = async (document) => {
            try {
                const r = await addPublishingTagsToModel(user,
                    damsId,
                    document,
                    [selectedSystem],
                    museums,
                    museumCollections,
                    null,
                    handleSaveTagError);
                taggedDocuments.push(r);
            } catch (error) {
                publishingErrors.push({'error': error, 'document': document});
            }
        };

        // Tag all documents first (single call for the first, pooled for the rest, as before).
        // NB publishing is deferred until every document has been tagged, so that a multi-select
        // "Publisering" action results in exactly one call to /nb/publish instead of one per
        // document.
        const firstDocument = documentsToTag.shift();
        await tagDocument(firstDocument);

        await PromisePool
            .withConcurrency(3)
            .for(documentsToTag)
            //.onTaskFinished((document, pool) => {
            //const percentage = parseInt(pool.processedPercentage());
            // TODO: dispatch to progress here!
            //})
            .handleError(async (error, document) => {
                return publishingErrors.push({'error': error, 'document': document});
            })
            .process(tagDocument);

        // Publish to NB through a background publish job (DE-1698), for one document or many.
        const nbDocuments = taggedDocuments.filter(document =>
            getPublishTags(document).some(t => t.publishingType === "publishing-nb"));

        let outcomeUnknown = 0;
        if (nbDocuments.length > 0) {
            // The job runs on the server whether or not this dialog stays open, so let it be closed.
            if (callback && typeof callback === 'function') {
                callback();
            }
            setNbProgress({completed: 0, total: nbDocuments.length});
            const publishResults = await publishToNbBatch(nbDocuments, {onProgress: setNbProgress});
            setNbProgress(null);

            let failed = 0;
            publishResults.forEach(({model: document, result}) => {
                if (result.status === 'published') {
                    setSelectedOptions(prev => [...prev, document]);
                } else if (result.status === 'unknown') {
                    // Still being published: keep the tag, the job may yet succeed.
                    outcomeUnknown++;
                } else {
                    failed++;
                    publishingErrors.push({
                        'error': new Error(result.error || 'failed to publish to NB-API'),
                        'document': document
                    });
                }
            });
            if (failed > 0) {
                showNbFailures(failed, publishResults.length);
            }
            if (outcomeUnknown > 0) {
                showNbOutcomeUnknown(outcomeUnknown, publishResults.length);
            }
        }

        if (publishingErrors.length > 0) {
            handlePublishingErrors(publishingErrors, selectedSystem);
        } else if (outcomeUnknown === 0) {
            enqueueSnackbar(`Publisering til ${system.label} fullført`, {variant: 'success'});
        }

        if (callback && typeof callback === 'function') {
            callback();
        }
    };

    const handlePublishingErrors = (errorObjects, publishingSystems) => {
        // Rollback, by removing failed publishing tags from the models in the UI:
        errorObjects.forEach(({document}) => {
            const {uniqueId} = document;
            const model = Array.isArray(models) ? models.find(m => m.uniqueId === uniqueId) : models;
            if (!model) {
                return;
            }

            const publishingTypes = Array.isArray(publishingSystems)
                ? publishingSystems.map(p => p.publishingType)
                : [publishingSystems.publishingType];

            model.content = model.content || {};
            model.content.publish = model.content.publish || {};
            model.content.publish.tags =
                getPublishTags(model)
                    .filter(t => !publishingTypes.map(pt => `publishing-${pt}`).includes(t.publishingType));
        });
    };

    const handlePublishClick = async (system) => {
        setPublishing(prev => new Set(prev).add(system.label));
        await publishToSystem(system);
        setPublishing(prev => {
            const next = new Set(prev);
            next.delete(system.label);
            return next;
        });
        setPublishedThisSession(prev => ([...prev, system]));

        // Refresh the search results to show updated publishing tags for the current model:
        if (!Array.isArray(models)) {
            const updatedModel = models;
            searchDispatch({
                type: MODELS_SAVED,
                models: [updatedModel],
            });
            searchResultDetailsDispatch({
                type: MODEL_SAVED,
                model: updatedModel,
            });
            searchDispatch({
                type: SET_SEARCHING
            });
        }
    };

    const handleUnpublishClick = async () => {
        try {
            setUnpublishing(true);

            // Unpublish from EMS:
            const updatedModel = await damsFetch('/publishing/unpublish/', {
                method: 'POST',
                body: JSON.stringify(decamelizeKeysDeep({
                    "unique_id": models.uniqueId,
                    "publishingType": 'publishing-ems'
                })),
            });

            // Show status snackbar:
            enqueueSnackbar(t('unpublishFromEmsSuccessBody', 'Objektet er avpublisert fra EMS.'), {variant: 'success'});

            // Update UI:
            const ix = selectedOptions.findIndex(o => o.publishingType === 'publishing-ems');
            if (ix > -1) {
                const opts = [...selectedOptions].toSpliced(ix, 1);
                setSelectedOptions(opts);
            }

            if (!Array.isArray(models)) {
                searchDispatch({
                    type: MODELS_SAVED,
                    models: [updatedModel],
                });
                searchResultDetailsDispatch({
                    type: MODEL_SAVED,
                    model: updatedModel,
                });
                searchDispatch({
                    type: SET_SEARCHING
                });
            }
        } catch (error) {
            clientLog('error', `Failed to unpublish from EMS: ${error.message}`, 'publishToSystemTable');
            enqueueSnackbar(t('unpublishFromEmsErrorBody', 'Det oppsto en feil ved avpublisering fra EMS.'), {variant: 'error'});
        } finally {
            setUnpublishing(false);
        }
    };

    const handleSaveTagError = () => {
        clientLog('error',
            'failed to save selected applications as publishing tags',
            'panelpublishtoems');
        enqueueSnackbar(t('savePublishingTagsErrorBody', 'Det oppstod en feil ved lagring av valgte tags.'),
            {variant: 'error'});
    };

    // Build a Set of already-published types for O(1) lookups inside renderOptions.
    const publishedTypeSet = useMemo(
        () => new Set(
            selectedOptions
                .filter(s => typeof s.publishingType !== 'undefined')
                .map(s => s.publishingType.replace('publishing-', ''))
        ),
        [selectedOptions]
    );

    const isAlreadyPublished = (publishingType) => publishedTypeSet.has(publishingType);

    const renderedOptions = useMemo(() => {
        const getPublishButton = (batchMode, option, disabled, alreadyPublished, publishedInThisSession) => {
            const {collectionId} = models ?? {};
            const {publishingType} = option;

            const button = <Button key={`button-system-publish-${publishingType}`}
                                   size="small"
                                   variant="contained"
                                   startIcon={<PublishIcon/>}
                                   loading={disabled}
                                   disabled={disabled || alreadyPublished || publishedInThisSession}
                                   onClick={async () => {
                                       await handlePublishClick(option);
                                   }}>
                {t('lblPublish', 'Publiser')}
            </Button>;

            if (batchMode) {
                // Always show the publish button in batch-mode:
                return button;
            } else {
                // Show the publish button only for systems the user has access to when publishing a single model:
                if (publishingType === 'nb') {
                    if (collectionId && getMuseumsWithNbAccess().find(m => m.collectionId === collectionId)) {
                        return button;
                    }
                } else if (publishingType === 'ems') {
                    if (collectionId && getMuseumsWithM24Access().find(m => m.collectionId === collectionId)) {
                        return button;
                    }
                }
            }
        };

        const getUnpublishButton = (batchMode, alreadyPublished, publishedInThisSession) => {
            const {collectionId} = models ?? {};
            const museumsWithM24Access = getMuseumsWithM24Access();
            const hasAccess = museumsWithM24Access.find(m => m.collectionId === collectionId);
            const successPhase = models?.content?.publish?.tags?.find(t => t.publishingType === 'publishing-ems')?.sync?.phases?.find(p => p.status === 'success');

            if (alreadyPublished && !batchMode && !publishedInThisSession && collectionId && hasAccess && successPhase) {
                return <Button
                    disabled={unpublishing}
                    onClick={async () => await handleUnpublishClick()}>
                    {t('btnUnpublish', 'Avpubliser')}
                </Button>;
            } else {
                return <></>;
            }
        };

        return options.map((option) => {
                const {label, publishingType} = option;
                const alreadyPublished = isAlreadyPublished(publishingType);

                const disabled = publishing.has(label);
                const publishedInThisSession = Boolean(publishedThisSession.find(o => o.label === label));

                if (alreadyPublished) {
                    return (
                        <Stack
                            key={`system-option-${publishingType}`}
                            direction="row"
                            sx={{
                                justifyContent: "space-between",
                                alignItems: "center",
                                font: 'bold',
                                width: '100%',
                                padding: '8px'
                            }}>
                            <Box>{label}</Box>
                            <Box>{t('lblPublished', 'Publisert')}</Box>
                            {getUnpublishButton(batchMode, alreadyPublished, publishedInThisSession)}
                        </Stack>
                    );
                } else {
                    return (
                        <Stack
                            key={`system-option-${publishingType}`}
                            direction="row"
                            sx={{
                                justifyContent: "space-between",
                                alignContent: "center",
                                font: 'bold',
                                width: '100%',
                                padding: '8px'
                            }}>
                            <Box>{option.label}</Box>
                            {(publishedInThisSession && batchMode) && <Box>{t('lblPublished', 'Publisert')}</Box>}

                            {getPublishButton(batchMode, option, disabled, alreadyPublished, publishedInThisSession)}
                        </Stack>
                    );
                }
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, publishedTypeSet, publishing, publishedThisSession, batchMode, unpublishing, t]);

    const showPublishingPanel = useMemo(() => {
        if (!models) {
            // No model available yet (e.g. still resolving) - nothing to publish.
            return false;
        } else if (Array.isArray(models)) {
            // If in batch-mode, always show the publishing panel,
            // as publishing rights may differ between models and are handled individually when publishing.
            return true;
        } else if (!Array.isArray(models)) {
            // If single model, only show the publishing panel if user has access to publish to at least one system.
            const {collectionId} = models;
            const hasNbAccess = getMuseumsWithNbAccess().find(m => m.collectionId === collectionId);
            const hasM24Access = getMuseumsWithM24Access().find(m => m.collectionId === collectionId);
            return hasNbAccess || hasM24Access;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [models, getMuseumsWithNbAccess, getMuseumsWithM24Access]);

    // Updates the list of museums when `myMuseums` changes.
    useEffect(() => {
        if (!myMuseums || myMuseums?.length === 0) {
            return;
        }
        setMuseums(myMuseums);
    }, [myMuseums]);

    // Fetches publishing tags and initializes selected tags from the model on component mount.
    useEffect(() => {
        if (!loading || !models || models.length === 0) {
            return;
        }

        /**
         * Filters publishing options based on the user's access rights.
         * @type {function(*, *): any}
         */
        const filterOptionsForAccess = ({objects, tags, museumsWithNbAccess, museumsWithM24Access}) => {
            const hasNBPublishingAccess = objs => {
                let state = false;
                if (museumsWithNbAccess && museumsWithNbAccess.length > 0 && objs) {
                    if (Array.isArray(objs)) {
                        state = objs.some(m => museumsWithNbAccess.find(ma => ma.collectionId === m.collectionId));
                    } else {
                        state = museumsWithNbAccess.find(m => m.collectionId === objs.collectionId);
                    }
                }
                return state;
            };

            const hasM24PublishingAccess = objs => {
                let state = false;
                if (museumsWithM24Access && museumsWithM24Access.length > 0 && objs) {
                    if (Array.isArray(objs)) {
                        state = objs.some(m => museumsWithM24Access.find(ma => ma.collectionId === m.collectionId));
                    } else {
                        state = museumsWithM24Access.find(m => m.collectionId === objs.collectionId);
                    }
                }
                return state;
            };

            if (!hasNBPublishingAccess(objects)) {
                tags = tags.filter(pt => pt.publishingType !== 'nb');
            }

            if (!hasM24PublishingAccess(objects)) {
                tags = tags.filter(pt => pt.publishingType !== 'ems');
            }

            return tags;
        };

        (async () => {
            // Fetch publishing tags from the database.
            let publishingTags = await publishingTagsSearchHandler();

            if (!publishingTags || publishingTags.length === 0) {
                clientLog('warning', 'No publishing tags found in the system.', 'publishtosystemtable');
                setLoading(false);
                setOptions([]);
                return;
            }

            clientLog('debug', `Fetched ${publishingTags.length} publishing tags.`, 'publishtosystemtable');

            publishingTags = filterOptionsForAccess({
                "objects": models,
                "tags": publishingTags,
                "museumsWithNbAccess": getMuseumsWithNbAccess(),
                "museumsWithM24Access": getMuseumsWithM24Access()
            });
            clientLog('debug', `After access filtering, ${publishingTags.length} publishing tags remain.`, 'publishtosystemtable');
            setOptions(publishingTags);

            // Load already selected tags from the model - not available in batch-mode!
            if (!Array.isArray(models)) {
                const optionsInModel = models?.content?.publish?.tags?.map(t => ({
                    label: t.reference.title,
                    publishingType: t.publishingType
                })) || [];
                setSelectedOptions(optionsInModel || []);
                setLoading(false);
            } else {
                setLoading(false);
            }
        })();
    }, [getMuseumsWithNbAccess, getMuseumsWithM24Access, models, loading]);

    if (loading) {
        return <LinearProgress variant="indeterminate" sx={{width: '100%', marginTop: '16px'}} color="primary"/>;
    } else if (!loading && showPublishingPanel) {
        return (
            <Grow in={true} timeout={300}>
                <Stack direction="column"
                       sx={{
                           border: '1px solid #ddd',
                           borderRadius: '4px',
                           marginTop: '16px',
                           width: '100%',
                       }}>
                    <Stack
                        direction="row"
                        sx={{
                            justifyContent: "space-between",
                            alignContent: "center",
                            width: '100%',
                            marginBottom: '8px',
                            borderBottom: '1px solid #ddd'
                        }}>
                        <Box sx={{padding: '8px'}}><b>{t('headingPublishingSystem', 'System')}</b></Box>
                    </Stack>
                    {renderedOptions}
                    {nbProgress && <>
                        <LinearProgress variant="indeterminate" sx={{width: '100%'}} color="primary"/>
                        <div role="status" aria-live="polite" style={{padding: '8px'}}>
                            {t('publishToNbProgress',
                                `Publiserer til Nasjonalbiblioteket: ${nbProgress.completed} av ${nbProgress.total} ` +
                                'dokumenter. Dette kan ta noen minutter. Du kan lukke vinduet – publiseringen ' +
                                'fortsetter i bakgrunnen.')}
                        </div>
                    </>}
                </Stack>
            </Grow>
        );
    }
};